/**
 * AuthSuccess - OAuth callback success page
 * HISI-FE-AUTH-01: Updated for cookie-based authentication
 * Handles OAuth code exchange and redirects after successful authentication
 */

import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingScreen from '@/components/ui/LoadingScreen';
import { joinApiPath } from '@/lib/api-config';

/**
 * Validate redirect URL to prevent open redirects
 * Only allows relative paths and same-origin URLs
 */
function validateRedirectUrl(redirectUrl: string): string {
  try {
    // Default fallback
    const defaultUrl = '/dashboard';

    // If empty or null, use default
    if (!redirectUrl || redirectUrl.trim() === '') {
      return defaultUrl;
    }

    // If it starts with /, it's a relative path - validate it's reasonable
    if (redirectUrl.startsWith('/')) {
      // Allow only reasonable paths (no .. traversal, no weird protocols)
      if (redirectUrl.includes('..') || redirectUrl.includes('://') || redirectUrl.includes('\\')) {
        console.warn('🔒 AuthSuccess: Suspicious redirect path, using default:', redirectUrl);
        return defaultUrl;
      }
      return redirectUrl;
    }

    // If it's a full URL, verify it's same origin
    const url = new URL(redirectUrl);
    const currentOrigin = window.location.origin;

    if (url.origin !== currentOrigin) {
      console.warn('🔒 AuthSuccess: Cross-origin redirect blocked, using default:', redirectUrl);
      return defaultUrl;
    }

    // Return the pathname + search + hash (strip origin)
    return url.pathname + url.search + url.hash;
  } catch (error) {
    console.warn('🔒 AuthSuccess: Invalid redirect URL, using default:', redirectUrl, error);
    return '/dashboard';
  }
}

export default function AuthSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) return;

    const handleAuthSuccess = async () => {
      hasProcessedRef.current = true;
      console.log('🎯 AuthSuccess: Processing OAuth code exchange...');
      try {
        // Get OAuth authorization code and redirect_to from URL params
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        let redirectTo = searchParams.get('redirect_to') || '/dashboard';

        console.log('🔍 AuthSuccess: OAuth callback params...', {
          hasCode: !!code,
          hasState: !!state,
          redirectTo
        });

        if (!code) {
          console.error('❌ AuthSuccess: No authorization code found in OAuth callback');
          navigate('/login?error=missing_code', { replace: true });
          return;
        }

        console.log('🔄 AuthSuccess: Exchanging code for session cookies...');
        // Exchange authorization code for HttpOnly cookies via backend
        const response = await fetch(joinApiPath('auth', 'callback'), {
          method: 'POST',
          credentials: 'include', // HISI-FE-AUTH-01: Cookie-based auth
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ message: 'Code exchange failed' }));
          console.error('❌ AuthSuccess: Code exchange failed:', error);
          navigate('/login?error=code_exchange_failed', { replace: true });
          return;
        }

        console.log('✅ AuthSuccess: Session cookies set by backend');

        // Validate redirect_to is path-only to prevent open redirects
        const redirectUrl = decodeURIComponent(redirectTo);
        const safeRedirectUrl = validateRedirectUrl(redirectUrl);

        console.log('🚀 AuthSuccess: Redirecting to:', safeRedirectUrl);

        // Clean URL and redirect to final destination with history cleanup
        navigate(safeRedirectUrl, { replace: true });

        // Remove OAuth params from browser history
        try {
          window.history.replaceState({}, '', safeRedirectUrl);
        } catch (error) {
          console.warn('⚠️ AuthSuccess: Failed to clean history state:', error);
        }
      } catch (error) {
        console.error('❌ AuthSuccess: Auth success handling failed:', error);
        navigate('/login?error=auth_processing_failed', { replace: true });
      }
    };

    handleAuthSuccess();
  }, [searchParams, navigate]);

  return <LoadingScreen />;
}